/**
 * The end of onboarding, and the answer to the four questions the product exists to answer:
 * what have we learned, what needs confirming, what is still missing, what conflicts.
 */

import { useMemo } from "react";
import { Link } from "react-router-dom";

import { knowledge as knowledgeApi, projects as projectsApi } from "../api/resources";
import { KnowledgeLegend, KnowledgeStatusBadges } from "../components/KnowledgeStatus";
import {
  Card,
  EmptyState,
  ErrorBanner,
  Loading,
  PageHeader,
  Progress,
  Stat,
  StatusBadge,
  Triple,
} from "../components/ui";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import { predicateLabel } from "../lib/format";
import { bucketFacts } from "../lib/knowledgeStatus";
import { useAsync } from "../lib/useAsync";

export function OnboardingReviewPage() {
  const { organizationId, organization, project } = useCurrentOrganization();

  const facts = useAsync(
    () =>
      organizationId
        ? knowledgeApi.facts(organizationId, { limit: 500 })
        : Promise.resolve({ items: [], total: 0, limit: 0, offset: 0 }),
    [organizationId],
  );
  const contradictions = useAsync(
    () => (organizationId ? knowledgeApi.contradictions(organizationId) : Promise.resolve([])),
    [organizationId],
  );
  const gaps = useAsync(
    () =>
      organizationId && project
        ? projectsApi.gaps(organizationId, project.id, { status_filter: "open" })
        : Promise.resolve([]),
    [organizationId, project?.id],
  );

  const conflictingIds = useMemo(() => {
    const ids = new Set<string>();
    for (const row of contradictions.data ?? []) {
      if (row.status !== "open") continue;
      ids.add(row.fact_a_id);
      ids.add(row.fact_b_id);
    }
    return ids;
  }, [contradictions.data]);

  const buckets = useMemo(
    () => bucketFacts(facts.data?.items ?? [], conflictingIds),
    [facts.data, conflictingIds],
  );

  if (!organizationId) {
    return (
      <Card>
        <EmptyState title="No organization selected" />
      </Card>
    );
  }

  if (facts.loading) return <Loading label="Gathering what we know…" />;

  const openContradictions = (contradictions.data ?? []).filter((row) => row.status === "open");
  const openGaps = gaps.data ?? [];
  const requiredGaps = openGaps.filter((gap) => gap.severity === "required");

  return (
    <>
      <PageHeader
        title="What we know so far"
        description={`A summary of everything discovered about ${
          organization?.dba_name ?? organization?.legal_name ?? "this company"
        }. Nothing here is treated as true until someone confirms it.`}
        actions={
          <>
            <Link className="btn" to="/knowledge">
              Review item by item
            </Link>
            <Link className="btn btn--primary" to="/interviews">
              Continue onboarding
            </Link>
          </>
        }
      />

      <div className="stack">
        <ErrorBanner error={facts.error ?? contradictions.error ?? gaps.error} />

        {project ? (
          <Card>
            <div className="row" style={{ justifyContent: "space-between" }}>
              <div>
                <strong>{project.name}</strong>
                <div className="small muted">
                  {requiredGaps.length === 0
                    ? "Everything required has been answered."
                    : `${requiredGaps.length} required things are still unknown.`}
                </div>
              </div>
              <div style={{ width: 300 }}>
                <Progress value={Number(project.completion_percentage)} />
              </div>
            </div>
          </Card>
        ) : null}

        <div className="grid grid--stats">
          <Stat label="Confirmed" value={buckets.confirmed.length} tone="ok" />
          <Stat label="Needs review" value={buckets.needsReview.length} tone="warn" />
          <Stat label="Missing" value={openGaps.length} tone={requiredGaps.length ? "danger" : undefined} />
          <Stat
            label="Conflicting"
            value={openContradictions.length}
            tone={openContradictions.length ? "danger" : undefined}
          />
        </div>

        <KnowledgeLegend />

        <div className="review-group review-group--confirmed">
          <Card title={`Confirmed (${buckets.confirmed.length})`} flush>
            {buckets.confirmed.length === 0 ? (
              <EmptyState
                title="Nothing confirmed yet"
                description="Facts move here once a person has checked them."
              />
            ) : (
              <ul className="checklist" style={{ padding: "6px 16px" }}>
                {buckets.confirmed.slice(0, 40).map((fact) => (
                  <li key={fact.id}>
                    <KnowledgeStatusBadges fact={fact} showOrigin={false} />
                    <Triple
                      subject={fact.subject_name}
                      predicate={predicateLabel(fact.predicate)}
                      object={fact.object_value}
                    />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="review-group review-group--review">
          <Card
            title={`Needs review (${buckets.needsReview.length})`}
            actions={
              buckets.needsReview.length > 0 ? (
                <Link className="btn btn--small btn--primary" to="/knowledge">
                  Start reviewing
                </Link>
              ) : null
            }
            flush
          >
            {buckets.needsReview.length === 0 ? (
              <EmptyState title="Nothing waiting" description="Every claim has been looked at." />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>What we heard or read</th>
                      <th>Where it came from</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buckets.needsReview.slice(0, 60).map((fact) => (
                      <tr key={fact.id}>
                        <td>
                          <Triple
                            subject={fact.subject_name}
                            predicate={predicateLabel(fact.predicate)}
                            object={fact.object_value}
                          />
                        </td>
                        <td className="small muted truncate">{fact.evidence_reference}</td>
                        <td>
                          <KnowledgeStatusBadges fact={fact} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <div className="review-group review-group--missing">
          <Card title={`Missing (${openGaps.length})`} flush>
            {openGaps.length === 0 ? (
              <EmptyState
                title="Nothing missing"
                description="Everything the selected areas expect to learn has an answer."
              />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>What we still need</th>
                      <th>About</th>
                      <th>Importance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openGaps.slice(0, 60).map((gap) => (
                      <tr key={gap.id}>
                        <td>{gap.title}</td>
                        <td className="small muted">
                          {gap.subject_name ?? gap.subject_type ?? "the company"}
                        </td>
                        <td>
                          <StatusBadge status={gap.severity} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {openGaps.length > 0 ? (
              <div style={{ padding: "10px 16px" }}>
                <Link to="/gaps">Work through what is missing →</Link>
              </div>
            ) : null}
          </Card>
        </div>

        <div className="review-group review-group--conflicting">
          <Card title={`Conflicting (${openContradictions.length})`} flush>
            {openContradictions.length === 0 ? (
              <EmptyState
                title="No conflicts"
                description="No two sources disagree about anything single-valued."
              />
            ) : (
              <div className="stack" style={{ padding: 16 }}>
                {openContradictions.map((row) => (
                  <div key={row.id}>
                    <strong>
                      {row.subject_name} — {predicateLabel(row.predicate)}
                    </strong>
                    <div className="grid grid--two" style={{ marginTop: 8 }}>
                      {[row.fact_a, row.fact_b].map((fact, index) =>
                        fact ? (
                          <div
                            key={fact.id}
                            style={{
                              border: "1px solid var(--border)",
                              borderRadius: 6,
                              padding: 10,
                            }}
                          >
                            <div className="row" style={{ justifyContent: "space-between" }}>
                              <span className="tiny faint">{index === 0 ? "Source A" : "Source B"}</span>
                              <KnowledgeStatusBadges fact={fact} conflicting />
                            </div>
                            <Triple
                              subject={fact.subject_name}
                              predicate={predicateLabel(fact.predicate)}
                              object={fact.object_value}
                            />
                            <div className="tiny faint" style={{ marginTop: 4 }}>
                              {fact.evidence_reference}
                            </div>
                          </div>
                        ) : null,
                      )}
                    </div>
                  </div>
                ))}
                <Link to="/gaps">Resolve conflicts →</Link>
              </div>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
